import getPoint from '../data/getPoint';
import * as element from '../util/element';

const MARGIN_ONLINE = 16;
const MARGIN_PRINT = 8;
const COLOR = '#333';

export function postRender(highchart, options) {
  if (options.annotations && options.annotations.length) {
    // Get values used for positioning.
    const suffix = options.axes.units.suffix;
    const liftUp = options.axes.showRecessions ||
      (suffix && options.axes.units.suffix.length > 2);

    const styleLH = parseFloat(options.annotations[0].element.style.lineHeight);
    if (styleLH) {
      options.annotations[0].element.style.lineHeight = `${styleLH}px`;
    }
    const lineHeight = parseFloat(
      window.getComputedStyle(options.annotations[0].element, null)
      .getPropertyValue('line-height')) || 16;
    const xy = getStartXY(highchart, options, suffix, liftUp);
    // Position and append items.
    const g = highchart.renderer.g('annotations').add();
    g.element.parentElement.appendChild(g.element);
    writeIdealPositions(highchart, options);
    spaceOutPositions(highchart, options, xy);
    appendItems(highchart, options, g, xy, lineHeight);

    // Make it crisp.
    if (options.product === 'wsj' && !options.print) g.element.setAttribute('transform', 'translate(0.5, -7)');
    else g.element.setAttribute('transform', 'translate(0.5, -4)');
    
    setStyles(g.element, options);
  }
}

function setStyles(element, options){
  if (__WSJ__) {
    if (options.product === 'wsj') {
      const text = element.childNodes;
      for (let b = 0; b < text.length; b++) {
        if (text[b].tagName === 'text') {
          text[b].style.fontSize = options.styles.annotations.fontSize;
          text[b].style.lineHeight = options.styles.annotations.lineHeight;
        }
      }
    }
  }
}

// Finds the X position for each annotation without regard to the other
// annotations that it overlaps or the edges of the chart.
function writeIdealPositions(highchart, options) {
  options.annotations.forEach((item, index) => {
    const rect = item.element.getBoundingClientRect();
    const xy = getGraphicXY(highchart, options, item);
    item.position = {
      gx: xy.x,
      gy: xy.y,
      tx: xy.x,
      ty: 0,
      width: rect.width * 1.154,
      height: rect.height,
      show: true,
    };
  });
}

function spaceOutPositions(highchart, options, xy) {
  const MARGIN = options.print ? MARGIN_PRINT : MARGIN_ONLINE;
  const hardLeft = Math.round(xy.x);
  const hardRight = highchart.plotLeft + highchart.plotWidth;
  const hardWidth = hardRight - hardLeft;
  const sortedA = options.sortedAnnotations = getSortedAnnotations(options);
  const recessionEl = highchart.container.querySelector('.label-recession');
  const recession = recessionEl ?
    {
      type: 'recession',
      startX: getStartX(recessionEl),
      endX: getStartX(recessionEl) + getWidth(recessionEl),
      midpoint: getStartX(recessionEl) + getWidth(recessionEl) / 2
    }
    :
    null;
  const suffixEl = highchart.container.querySelector('.y-axis-suffix');
  const suffix = suffixEl ?
    {
      type: 'suffix',
      endX: getStartX(suffixEl) + getWidth(suffixEl)
    }
    :
    null;

  // Hide annotations that are too wide to fit, even all by themselves.
  sortedA.forEach(item => {
    const pos = item.position;
    pos.tx = Math.max(pos.tx, hardLeft);
    if (pos.tx + pos.width > hardRight) {
      pos.tx = hardRight - pos.width;
      if (pos.tx < hardLeft) {
        pos.show = false;
      }
    }
  });

  // Hide annotations for which the width of the entire chart will not
  // accomodate them.
  let accumWidth = 0;
  options.annotations.forEach(item => {
    if (item.position.show) {
      accumWidth += item.position.width + MARGIN;
      if (accumWidth > hardWidth) {
        item.position.show = false;
      }
    }
  });

  let softPx = hardLeft;
  let tx, midpoint, lineTopX, overlap, labelMidpoint;
  // Shift items left to right.
  sortedA.forEach(item => {
    midpoint = item.position.width / 2;
    if (item.position.show) {
      item.position.tx = Math.max(item.position.tx, softPx);
      if (recession || suffix) {
        [lineTopX, overlap] = checkOverlap(item.position, highchart.plotLeft, recession, suffix);
        // does line overlap with recession or suffix label, and if recession, should it shift right
        if (overlap.type === 'suffix' || (overlap.type === 'recession' && lineTopX >= recession.midpoint)) {
          // initially center annotation above label right edge (with 2px cushion)
          tx = overlap.endX - midpoint + 2;
          adjustPosition(item, highchart, tx, midpoint, 'right');
        };
      };
      softPx = item.position.tx + item.position.width + MARGIN;
    }
  });
  // Shift items right to left.
  softPx = hardRight + MARGIN;
  for (let i = sortedA.length - 1; i >= 0; i--) {
    const item = sortedA[i];
    midpoint = item.position.width / 2;
    if (item.position.show) {
      item.position.tx = Math.min(item.position.tx, softPx - item.position.width - MARGIN);
      if (recession) {
        [lineTopX, overlap] = checkOverlap(item.position, highchart.plotLeft, recession, suffix);
        // shift annotation left if line overlaps with recession label
        if (overlap.type === 'recession') {
          // initially center annotation above recession label left edge (with 2px cushion)
          tx = recession.startX - midpoint - 2;
          adjustPosition(item, highchart, tx, midpoint, 'left');
        };
        // hide item if pushed off left edge of chart
        if (item.position.tx < highchart.plotLeft) item.position.show = false;
      };
      checkSuffix(item, highchart.plotLeft, recession, suffix);
      softPx = item.position.tx;
    }
  }
}


function appendItems(highchart, options, g, xy, lineHeight) {
  // Append and position.
  options.annotations.forEach((item, index) => {
    const el = item.element;
    if (item.position.show) {
      const x = txShift(item.position);
      const y = xy.y;
      let textX = item.position.tx;
      let textAnchor = 'start';

      if (item.flushAlign === 'flush') {
        if (x >= textX && textX <= Math.round(xy.x)) {
          item.textAlign = 'left';
        } else if (textX <= x && textX + item.position.width >=
            highchart.plotLeft + highchart.plotWidth) {
          item.textAlign = 'right';
        }
      }
      if (item.textAlign === 'right') {
        textAnchor = 'end';
        textX += item.position.width;
      } else if (item.textAlign === 'center') {
        textAnchor = 'middle';
        textX += item.position.width / 2;
      }
      const tspanLen = writeTspanX(el.children, textX);
      const offsetY = -lineHeight * tspanLen;
      item.position.tx = x;
      item.position.ty = y;
      el.setAttribute('text-anchor', textAnchor);
      el.setAttribute('x', textX);
      el.setAttribute('y', y + offsetY);
      g.element.appendChild(el);

    } else {
      element.removeChild(el);
    }
  });
  drawItemLines(highchart, options, g, xy);
}

// Get x, y where an annotation connector should attached to a chart graphic.
function getGraphicXY(highchart, options, item) {
  const p = item.attach.point;
  const c = item.attach.category;
  const xy = { x: 0, y: 0 };
  if (p.enabled) {
    const sIndex = options.series[p.series].hasOwnProperty('index') ?
      options.series[p.series].index : p.series;
    const pIndex = getPointIndex(options.series[sIndex].data, p.index);
    const point = options.irregularDates ? highchart.series[sIndex].points[pIndex] : highchart.series[sIndex].data[pIndex];
    if (options.type === 'column') {
      const shape = point.shapeArgs;
      xy.x = shape.x + Math.floor(shape.width / 2);
      xy.y = shape.y;
    } else if (options.type === 'area' || options.type === 'line') {
      xy.x = point.plotX;
      xy.y = point.plotY;
    }
  } else {
    const cIndex = getPointIndex(options.series[0].data, c.index);
    // xy.x = (cIndex / highchart.series[0].data.length) * highchart.plotWidth;
    xy.x = Math.floor(highchart.series[0].data[cIndex].clientX);
    xy.y = highchart.plotHeight;
  }
  return xy;
}

function getPointIndex(data, index) {
  let pointIndex = 0;
  data.some((item, i) => {
    if (item.index === index) {
      pointIndex = i
      return true;
    }
  });
  return pointIndex;
}

function writeTspanX(tspan, x) {
  let tspanLen = 0;
  for (let i = 0; i < tspan.length; i++) {
    if (tspan[i].nodeName === 'tspan') {
      tspanLen++;
      tspan[i].setAttribute('x', x);
    }
  }
  return tspanLen - 1;
}

function getStartXY(highchart, options, suffix, liftUp) {
  if (!liftUp && highchart.axes[1].opposite) {
    return { x: 0, y: highchart.plotTop - 7 };
  }
  const MARGIN = options.print ? MARGIN_PRINT : MARGIN_ONLINE;
  const suffixEl = suffix ?
    highchart.container.querySelector('.y-axis-suffix') : null;
  const xy = getTopAxisLabelXY(highchart, options);
  if (suffixEl && !liftUp) {
    // To the right of the suffix.
    return {
      x: +suffixEl.getAttribute('x') + suffixEl.getBoundingClientRect().width +
        MARGIN,
      y: +suffixEl.getAttribute('y'),
    };
  } else if (suffixEl && liftUp) {
    // Above the suffix but aligned left with it, but we also need to find out
    // if the suffix starts with a space to get the alignment right.
    let suffixClassName = false;
    if (suffixEl.children.length) {
      suffixClassName = suffixEl.children[0].getAttribute('class');
    }
    const offsetX = (suffixClassName === 'space') ? 4 : 0;
    return {
      x: +suffixEl.getAttribute('x') + offsetX,
      y: +suffixEl.getAttribute('y') - suffixEl.getBoundingClientRect().height,
    };
  }
  else if (liftUp) {
    const recessionEl = highchart.container.querySelector('.label-recession');
    return {
      x: highchart.axes[1].opposite ? 0 : xy.x + MARGIN,
      y: +recessionEl.getAttribute('y') - recessionEl.getBoundingClientRect().height
    };
  }
  else {
    // To the right of the y-axis label.
    xy.x += MARGIN;
    return xy;
  }
}

function getTopAxisLabelXY(highchart, options) {
  const axis = highchart.axes[1];
  const tickPos = axis.tickPositions[options.axes.reversed ?
    0 : (axis.tickPositions.length - 1)];
  return axis.ticks[tickPos].label.xy;
}

function drawItemLines(highchart, options, g, xy) {
  const MARGIN = options.print ? MARGIN_PRINT : MARGIN_ONLINE;
  const line = { x1: 0, x2: 0, y: xy.y + MARGIN };
  options.sortedAnnotations.forEach((item) => {
    if (item.position.show) {
      writePathPoints(highchart, item, MARGIN);
    }
  });

  // Bump jointed lines down when overlapping to left.
  let prevItem = null;
  let addPx = MARGIN / 2;
  options.sortedAnnotations.forEach((item) => {
    if (item.position.show) {
      if (item.path.length > 4) {
        if (prevItem && prevItem.position.gx < prevItem.position.tx &&
            prevItem.pathRect.x + prevItem.pathRect.width + MARGIN >= item.pathRect.x) {
          item.path[3] += addPx;
          item.path[5] += addPx;
          addPx += MARGIN / 2;
        }
        else {
          addPx = MARGIN / 2;
        }
        prevItem = item;
      }
    }
  });

  // Bump jointed lines down when overlapping to right.
  prevItem = null;
  options.sortedAnnotations.reverse().forEach((item) => {
    if (item.position.show) {
      if (item.path.length > 4) {
        if (prevItem && prevItem.position.gx > prevItem.position.tx &&
            prevItem.pathRect.x <= item.pathRect.x + item.pathRect.width + MARGIN) {
          item.path[3] += addPx;
          item.path[5] += addPx;
          addPx += MARGIN / 2;
        }
        else {
          addPx = MARGIN / 2;
        }
        prevItem = item;
      }
    }
  });

  options.sortedAnnotations.reverse().forEach((item) => {
    if (item.position.show) {
      drawLine(highchart, options, item, g);
    }
  });
}

function drawLine(highchart, options, item, g) {
  const p = item.attach.point;
  const c = item.attach.category;
  let lineEnd = item.lineEnd || options.defaults.annotations.lineEnd;
  const path = item.path.slice(0);
  let lineOffset = getOffsetLineEnd(lineEnd, options);
  if (lineEnd === 'nothing' && ((options.type === 'column' && c.enabled) ||
      options.type !== 'column')) {
    lineOffset = 0;
  } else if (path.length > 4 && path[path.length - 1] + lineOffset <
      path[path.length - 3]) {
    lineOffset = 0;
  }
  path.splice(0, 0, 'M');
  path.splice(3, 0, 'L');
  const isWSJIncreaseOffset = options.product === 'wsj' ? 4 : 0;
  path[path.length - 1] += lineOffset + isWSJIncreaseOffset;
  if (options.product === 'wsj') path[2] += 2;
  if (!(item.showLine === false)) {
    highchart.renderer.path(path)
      .attr({ 'stroke': COLOR, 'stroke-width': options.print ? 0.5 : 1 })
      .add(g);
  }
  drawLineEnd(highchart, g, path, lineEnd, options.print, options.product);
}

function drawLineEnd(highchart, g, path, lineEnd, isPrint, product) {
  let isWSJ = product === 'wsj' ? -2 : -1;
  isWSJ = isPrint ? -3 : isWSJ;
  const endPt = {
    x: path[path.length - 2],
    y: Math.ceil(path[path.length - 1]) + isWSJ
  };
  if (lineEnd === 'arrow') {
    drawLineEndArrow(highchart, g, endPt, isPrint);
  } else if (lineEnd === 'dot') {
    drawLineEndDot(highchart, g, endPt, isPrint);
  } else if (lineEnd === 'circle') {
    drawLineEndCircle(highchart, g, endPt, isPrint);
  }
}

function drawLineEndArrow(highchart, g, endPt, isPrint) {
  const size = isPrint ? 3.5 : 6.5;
  const side = Math.sqrt((size * size) - ((size/2) * (size/2)));
  const path = [
    'M', endPt.x - size * 0.5, endPt.y,
    'L', endPt.x + size * 0.5, endPt.y,
    endPt.x, endPt.y + side,
  ];
  highchart.renderer.path(path)
    .attr({
      fill: COLOR
    })
    .add(g);
}

function drawLineEndDot(highchart, g, endPt, isPrint) {
  const radius = isPrint ? 1.5 : 3;
  highchart.renderer.circle(endPt.x, endPt.y + 4, radius)
    .attr({
      fill: COLOR
    })
    .add(g);
}

function drawLineEndCircle(highchart, g, endPt, isPrint) {
  const radius = isPrint ? 4 : 8;
  highchart.renderer.circle(endPt.x, endPt.y + radius, radius)
    .attr({
      stroke: COLOR,
      'stroke-width': isPrint ? 0.5 : 1,
      fill: 'transparent',
    })
    .add(g);
}

function writePathPoints(highchart, item, MARGIN) {
  item.position.gx = gxShift(item.position.gx, highchart.plotLeft);
  item.position.gy += highchart.plotTop;
  item.position.gy = Math.round(item.position.gy);
  const ty = item.position.ty + 4;
  const jointY = Math.max(highchart.plotTop + MARGIN / 2,
    Math.min(ty + MARGIN, item.position.gy));
  const isStraight = checkPositions(item.position);
  const points = [isStraight ? item.position.gx : item.position.tx, ty];
  if (isStraight) {
    // Line descends straight from text to graphic.
    points.push(item.position.gx, item.position.gy);
  } else {
    // Line is not straight.
    points.push(item.position.tx, jointY);
    points.push(item.position.gx, jointY);    
  }
  if (item.position.gy > points[points.length - 1]) {
    // Descend from top of line/break to graphic.
    points.push(item.position.gx, Math.max(item.position.gy, jointY));
  }
  item.path = points;
  item.pathRect = getPathRect(points);
}

function getPathRect(points) {
  let x1 = Number.MAX_VALUE;
  let x2 = Number.MIN_VALUE;
  let y1 = Number.MAX_VALUE;
  let y2 = Number.MIN_VALUE;
  points.forEach((pt, index) => {
    if (index % 2 === 0) {
      x1 = (pt < x1) ? pt : x1;
      x2 = (pt > x2) ? pt : x2;
    } else {
      y1 = (pt < y1) ? pt : y1;
      y2 = (pt > y2) ? pt : y2;
    }
  });
  return {
    x: x1,
    y: y2,
    width: x2 - x1,
    height: y2 - y1,
  };
}

function getStartX(el) {
  return +el.getAttribute('x');
}

function getWidth(el) {
  return el.getBoundingClientRect().width;
}

function checkOverlap(position, margin, recession, suffix) {
  const lineTopX = findLineTop(position, margin);
  let overlap;
  if (recession && lineTopX >= recession.startX && lineTopX <= recession.endX) {
    overlap = recession;
  }
  else if (suffix && lineTopX <= suffix.endX) {
    overlap = suffix;
  }
  else {
    overlap = { type: 'none' };
  }
  return [lineTopX, overlap];
}

function findLineTop(position, margin) {
  // determine which x value will be used for top of line
  const gx = gxShift(position.gx, margin);
  const tx = txShift(position);
  const straightLine = checkPositions({ gx: gx, tx: tx, width: position.width });
  return straightLine ? gx : tx;
}

function txShift(position) {
  // shift top of line to middle of annotation
  return Math.round(position.tx + position.width / 2);
}

function gxShift(gx, margin) {
  // adjust for left edge of chart
  return Math.round(gx + margin);
}

function adjustPosition(item, highchart, tx, midpoint, direction) {
  // if needed, shift annotation so jointed line will wrap around recession label
  const gxShifted = gxShift(item.position.gx, highchart.plotLeft);
  const txShifted = txShift({ tx: tx, width: item.position.width });
  const deltaX = findDelta(gxShifted, txShifted);
  const straightLine = checkPositions({ gx: gxShifted, tx: txShifted, width: item.position.width });
  if (straightLine) {
    const multiplier = direction === 'left' ? -1 : 1;
    tx += (midpoint - deltaX) * multiplier;
  };
  item.position.tx = tx;
}

function findDelta(gx, tx) {
  return Math.abs(gx - tx);
}

function checkPositions(position) {
  const deltaX = findDelta(position.gx, position.tx);
  return deltaX <= position.width / 2;
}

function checkSuffix(item, margin, recession, suffix) {
  // hide item if pushed into suffix label
  const checkSuffix = checkOverlap(item.position, margin, recession, suffix);
  if (checkSuffix[1].type === 'suffix') item.position.show = false;
}

// The pointer line/connector must be adjusted per lineEnd type.
function getOffsetLineEnd(lineEnd, options) {
  switch (lineEnd) {
    case 'dot':
      return 0;
    case 'circle':
      return options.print ? -4 : -8;
    case 'arrow':
      return options.print ? -4.25 : -8.5;
    // case 'nothing':
    default:
      return options.print ? -1 : -2;
  }
}

function getSortedAnnotations(options) {
  return options.annotations.slice(0).sort((a, b) => {
    return sortOnPointIndex(a, b, options);
  });
}

// Sort so that annotations go left to right (smallest index to highest).
function sortOnPointIndex(a, b, options) {
  return getItemIndex(a, options) - getItemIndex(b, options);
}

// Return index either of point or of category for sorting.
function getItemIndex(annotation, options) {
  for (let i = 0, keys = Object.keys(annotation.attach); i < keys.length; i++) {
    if (annotation.attach[keys[i]].enabled) {
      const index = getPointIndex(options.series[0].data,
        annotation.attach[keys[i]].index);
      return index;
      // return annotation.attach[keys[i]].index;
    }
  }
}



// WEBPACK FOOTER //
// ./src/chart/inner/annotations.js